import { useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { Mail } from "lucide-react";
import { DELIVERY_LABEL, paths, type EmailDelivery } from "@clevis/shared-types";
import { Button, Input, Label } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call, CallError } from "../lib/callFunction";
import { useAuth } from "../hooks/useAuth";
import { useAccess } from "../hooks/useAccess";
import { textareaClass } from "./JobEditCard";
import { CompanyDocPicker, useCompanyDocs } from "./CompanyDocPicker";
import { useDocumentSets } from "./DocumentSetsCard";
import { expiryState } from "./SendDocuments";

// Email an estimate or invoice: the PDF plus any company documents
// (brochures, W-9, certificates), to one or more people, with CC / BCC.
// A draft is finalized first (an invoice's parts come out of stock then).
// The send lands on the document's email history and the job's timeline.

export interface EmailableDoc {
  id: string;
  number: number;
  status: string;
  customerId: string | null;
  emails?: Array<{ at: number; to: string[]; ok: boolean }>;
}

const split = (s: string) =>
  s
    .split(/[\s,;]+/)
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean);
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function EmailSalesDoc({ tenantId, kind, docRow, onDone, onClose }: { tenantId: string; kind: "estimate" | "invoice"; docRow: EmailableDoc; onDone: (msg: string) => void; onClose: () => void }) {
  const { user } = useAuth();
  const can = useAccess();
  const docs = useCompanyDocs(tenantId, docRow.customerId);
  const [company, setCompany] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCopies, setShowCopies] = useState(false);
  const [ccMe, setCcMe] = useState(true);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  // Document sets the company attaches to this kind of email (and, for estimates, this type) start ticked.
  const sets = useDocumentSets(tenantId);
  const [preTicked, setPreTicked] = useState(false);
  useEffect(() => {
    if (preTicked || !sets.length || !docs.length) return;
    const typeId = (docRow as { typeId?: string | null }).typeId ?? null;
    const ids = new Set<string>();
    for (const s of sets) {
      if (!s.useFor[kind === "estimate" ? "estimates" : "invoices"]) continue;
      if (kind === "estimate" && s.estimateTypeIds.length && (!typeId || !s.estimateTypeIds.includes(typeId))) continue;
      for (const id of s.docIds) if (docs.some((d) => d.id === id)) ids.add(id);
    }
    setPreTicked(true);
    if (ids.size) setPicked((p) => new Set([...p, ...ids]));
  }, [sets, docs, preTicked, kind, docRow]);
  const [allowExpired, setAllowExpired] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const label = kind === "estimate" ? "Estimate" : "Invoice";
  const isDraft = docRow.status === "draft";

  useEffect(() => {
    void getDoc(doc(db, paths.tenant(tenantId))).then((s) => setCompany((s.get("name") as string | undefined) ?? ""));
    if (docRow.customerId) {
      void getDoc(doc(db, paths.tenantCustomer(tenantId, docRow.customerId))).then((s) => {
        const e = ((s.get("email") as string | undefined) ?? "").trim().toLowerCase();
        setCustomerEmail(e);
        setTo((t) => t || e);
      });
    }
  }, [tenantId, docRow.customerId]);

  const recent = useMemo(() => {
    const seen = new Set<string>();
    return [customerEmail, ...(docRow.emails ?? []).flatMap((e) => e.to)]
      .filter((e) => e && EMAIL.test(e) && !seen.has(e) && (seen.add(e), true))
      .slice(0, 5);
  }, [customerEmail, docRow.emails]);
  const expiredPicked = docs.filter((d) => picked.has(d.id) && expiryState(d.expiresAt) === "expired");

  async function send() {
    const toList = split(to);
    const all = [...toList, ...split(cc), ...split(bcc)];
    const bad = all.filter((e) => !EMAIL.test(e));
    if (!toList.length) return setError("Add who it goes to.");
    if (bad.length) return setError(`Check ${bad.join(", ")}: that isn't an email address.`);
    if (expiredPicked.length && !allowExpired) return setError("One of the attachments has expired. Tick “send anyway” or untick it.");
    setBusy(true);
    setError("");
    try {
      if (isDraft) {
        await call(kind === "estimate" ? "setEstimateStatus" : "setInvoiceStatus", { tenantId, [kind === "estimate" ? "estimateId" : "invoiceId"]: docRow.id, action: "send" });
      }
      await call(
        "sendSalesDocEmail",
        {
          tenantId,
          docId: docRow.id,
          kind,
          to: toList,
          cc: split(cc),
          bcc: split(bcc),
          ccMe,
          subject: subject.trim(),
          message,
          attachDocIds: [...picked],
          allowExpired,
        },
        { timeoutMs: 120_000 },
      );
      onDone(`${label} #${docRow.number} emailed to ${toList.join(", ")}${picked.size ? ` with ${picked.size} document${picked.size === 1 ? "" : "s"}` : ""}.`);
    } catch (err) {
      const e = err as CallError;
      setError(e.hint ? `${e.message} ${e.hint}` : e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2 space-y-2 rounded-lg border border-steel/20 bg-cream/40 p-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-ink">
        <Mail size={14} /> Email {label.toLowerCase()} #{docRow.number}
        {isDraft ? <span className="rounded-full bg-brandorange/15 px-2 py-0.5 text-[11px] font-normal text-brandorange-dark">draft: it's finalized when you send</span> : null}
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label>To</Label>
          {!showCopies ? (
            <button type="button" className="text-xs text-steel underline hover:text-ink" onClick={() => setShowCopies(true)}>
              Add CC / BCC
            </button>
          ) : null}
        </div>
        <Input value={to} placeholder="customer@example.com, pm@builder.com" onChange={(e) => setTo(e.target.value)} />
        {recent.filter((e) => !split(to).includes(e)).length ? (
          <div className="mt-1 flex flex-wrap gap-1">
            {recent
              .filter((e) => !split(to).includes(e))
              .map((e) => (
                <button key={e} type="button" className="rounded-full bg-steel/10 px-2 py-0.5 text-[11px] text-ink hover:bg-steel/20" onClick={() => setTo((t) => [...split(t), e].join(", "))}>
                  + {e}
                </button>
              ))}
          </div>
        ) : null}
      </div>
      {showCopies ? (
        <div className="grid gap-2 sm:grid-cols-2">
          <div>
            <Label>CC</Label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} />
          </div>
          <div>
            <Label>BCC (hidden from the others)</Label>
            <Input value={bcc} onChange={(e) => setBcc(e.target.value)} />
          </div>
        </div>
      ) : null}
      <label className="flex items-center gap-2 text-xs text-ink">
        <input type="checkbox" checked={ccMe} onChange={(e) => setCcMe(e.target.checked)} />
        Copy me{user?.email ? ` (${user.email})` : ""}
      </label>
      <div>
        <Label>Subject</Label>
        <Input value={subject} placeholder={`${label} #${docRow.number} from ${company || "your company"}`} onChange={(e) => setSubject(e.target.value)} />
      </div>
      <div>
        <Label>Message (optional)</Label>
        <textarea className={textareaClass} rows={3} placeholder={`Hi, your ${label.toLowerCase()} is attached. Let us know if you have any questions.`} value={message} onChange={(e) => setMessage(e.target.value)} />
      </div>
      <div>
        <Label>Attach company documents (brochures, W-9, certificates)</Label>
        <CompanyDocPicker
          docs={docs}
          picked={picked}
          canManage={can("documents")}
          tenantId={tenantId}
          customerId={docRow.customerId}
          onAdded={(id) => setPicked((p) => new Set([...p, id]))}
          onToggle={(id) =>
            setPicked((p) => {
              const n = new Set(p);
              if (n.has(id)) n.delete(id);
              else n.add(id);
              return n;
            })
          }
        />
        {expiredPicked.length ? (
          <label className="mt-1 flex items-center gap-2 text-xs text-danger">
            <input type="checkbox" checked={allowExpired} onChange={(e) => setAllowExpired(e.target.checked)} />
            {expiredPicked.map((d) => d.name).join(", ")} {expiredPicked.length === 1 ? "has" : "have"} expired. Send anyway
          </label>
        ) : null}
      </div>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <div className="flex items-center gap-2">
        <Button busy={busy} onClick={() => void send()}>
          {isDraft ? `Finalize & email` : "Send email"}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function lastEmailed(
  emails?: Array<{ at: number; to: string[]; ok: boolean; id?: string | null }>,
  delivery?: Record<string, EmailDelivery>,
): string {
  const last = [...(emails ?? [])].reverse().find((e) => e.ok);
  if (!last) return "";
  const news = last.id ? delivery?.[last.id] : undefined;
  return `emailed ${new Date(last.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })} to ${last.to[0]}${last.to.length > 1 ? ` +${last.to.length - 1}` : ""}${news ? `, ${DELIVERY_LABEL[news.status]}` : ""}`;
}
